import React from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { updateCondition } from '../../../state';
import { useWorkflowValidationContext, getConditionError } from '../../shared';
import {
  useGetTicketCategoriesQuery,
  useGetTicketPrioritiesQuery,
  useGetTicketStatusesQuery,
  useGetTicketUrgenciesQuery,
  useGetCentersQuery,
  useGetIssueTypesQuery,
  useGetRepairableByOptionsQuery,
  useGetHstRolesQuery,
  useGetPMCategoriesQuery,
} from '../../../../../services/DimensionService/dimensionApi';
import { EnumView } from '../../../../../services/DimensionService/types/dimensionTypes';

interface ConditionValueInputProps {
  index: number;
}

// Map workflow ticket-field keys → which dimension hook to call.
// Keeps the UI in lock-step with the backend WorkflowContextBuilder, which emits
// the SmartEnum *value* (code) for every enum-backed field. Free-text fields
// (Title, Description, customer info, etc.) fall through to a TextField.
type DimHookResult = { data?: EnumView[]; isLoading: boolean };
const useDimensionForField = (field: string): DimHookResult | null => {
  // All hooks must run on every render — gate by `skip` instead of conditional calls.
  const cat = useGetTicketCategoriesQuery(undefined, { skip: field !== 'Identification.Category' });
  const pri = useGetTicketPrioritiesQuery(undefined, { skip: field !== 'PriorityInfo.Priority' });
  const sta = useGetTicketStatusesQuery(undefined, { skip: field !== 'Workflow.Status' });
  const urg = useGetTicketUrgenciesQuery(undefined, { skip: field !== 'PriorityInfo.Urgency' });
  const fac = useGetCentersQuery(undefined, { skip: field !== 'FacilityMaintenanceRequest.Center' });
  const cps = useGetCentersQuery(undefined, { skip: field !== 'CpsiAccessRequest.Center' });
  const pmc = useGetCentersQuery(undefined, { skip: field !== 'PreventiveMaintenanceRequest.CenterId' });
  const issue = useGetIssueTypesQuery(undefined, { skip: field !== 'FacilityMaintenanceRequest.IssueType' });
  const repair = useGetRepairableByOptionsQuery(undefined, { skip: field !== 'FacilityMaintenanceRequest.RepairableBy' });
  const pmRepair = useGetRepairableByOptionsQuery(undefined, { skip: field !== 'PreventiveMaintenanceRequest.PerformedBy' });
  const hst = useGetHstRolesQuery(undefined, { skip: field !== 'HstAccessRequest.Role' });
  const pmcat = useGetPMCategoriesQuery(undefined, { skip: field !== 'PreventiveMaintenanceRequest.Category' });

  switch (field) {
    case 'Identification.Category': return cat;
    case 'PriorityInfo.Priority': return pri;
    case 'Workflow.Status': return sta;
    case 'PriorityInfo.Urgency': return urg;
    case 'FacilityMaintenanceRequest.Center': return fac;
    case 'CpsiAccessRequest.Center': return cps;
    case 'PreventiveMaintenanceRequest.CenterId': return pmc;
    case 'FacilityMaintenanceRequest.IssueType': return issue;
    case 'FacilityMaintenanceRequest.RepairableBy': return repair;
    case 'PreventiveMaintenanceRequest.PerformedBy': return pmRepair;
    case 'HstAccessRequest.Role': return hst;
    case 'PreventiveMaintenanceRequest.Category': return pmcat;
    default: return null;
  }
};

export const ConditionValueInput: React.FC<ConditionValueInputProps> = ({ index }) => {
  const dispatch = useAppDispatch();
  const condition = useAppSelector((state) => state.workflow.currentWorkflow?.conditions[index]);
  const dim = useDimensionForField(condition?.field ?? '');
  const { errors, showErrors } = useWorkflowValidationContext();

  if (!condition) return null;

  const setValue = (value: string) =>
    dispatch(updateCondition({ index, field: 'value', value }));

  const valueErr = getConditionError(errors, showErrors, index, 'value');

  if (dim) {
    const options = dim.data ?? [];
    // Show the current saved value even if it isn't in the loaded options yet
    // (avoids the MUI "out-of-range" warning while options stream in).
    const valueIsKnown = options.some((o) => o.value === condition.value);
    const selectValue = !condition.value || valueIsKnown || dim.isLoading ? condition.value : '';

    return (
      <FormControl size="small" sx={{ flex: 1, minWidth: 200 }} error={!!valueErr}>
        <InputLabel>Value</InputLabel>
        <Select
          value={selectValue}
          label="Value"
          onChange={(e) => setValue(e.target.value as string)}
          disabled={dim.isLoading}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} title={o.value}>
              {o.name} ({o.value})
            </MenuItem>
          ))}
        </Select>
        {valueErr && <FormHelperText>{valueErr}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <TextField
      size="small"
      label="Value"
      value={condition.value}
      onChange={(e) => setValue(e.target.value)}
      error={!!valueErr}
      helperText={valueErr}
      sx={{ flex: 1 }}
    />
  );
};

export default ConditionValueInput;
